import { useRouterState } from '@tanstack/react-router';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cn } from '@/lib/utils';

interface SidePanelContextValue {
  /** Open the panel with the given content (replaces any current content). */
  open: (
    content: ReactNode,
    options?: { presentation?: 'docked' | 'overlay' }
  ) => void;
  /** Close the panel. */
  close: () => void;
  isOpen: boolean;
  presentation: 'docked' | 'overlay';
}

const SidePanelContext = createContext<SidePanelContextValue | null>(null);
const SidePanelContentContext = createContext<ReactNode | null>(null);

/**
 * Controls the global, non-modal detail panel. It docks beside the page by
 * default; pass `{ presentation: 'overlay' }` to float above the page without
 * changing its width. The body can call `close()` via this hook when done.
 */
export function useSidePanel(): SidePanelContextValue {
  const ctx = useContext(SidePanelContext);
  if (!ctx) {
    throw new Error('useSidePanel must be used within a SidePanelProvider');
  }
  return ctx;
}

export function SidePanelProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [presentation, setPresentation] = useState<'docked' | 'overlay'>(
    'docked'
  );
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = useCallback<SidePanelContextValue['open']>((node, options) => {
    if (clearTimer.current) clearTimeout(clearTimer.current);
    setContent(node);
    setPresentation(options?.presentation ?? 'docked');
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Keep content mounted until the slide-out finishes, then drop it so any
    // queries/forms inside unmount.
    if (clearTimer.current) clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => setContent(null), 200);
  }, []);

  useEffect(() => {
    return () => {
      if (clearTimer.current) clearTimeout(clearTimer.current);
    };
  }, []);

  // Close on route change — the panel content (and any injected callbacks) is
  // page-scoped, so it shouldn't linger across navigations.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const prevPathname = useRef(pathname);
  useLayoutEffect(() => {
    if (prevPathname.current !== pathname) {
      prevPathname.current = pathname;
      close();
    }
  }, [pathname, close]);

  // Escape closes the panel (it has no backdrop to click away on).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      // A dropdown or dialog handles its own Escape first. Keep the editor and
      // its unsaved changes open when that nested control dismissed itself.
      if (e.key === 'Escape' && !e.defaultPrevented) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  const value = useMemo(
    () => ({ open, close, isOpen, presentation }),
    [open, close, isOpen, presentation]
  );

  return (
    <SidePanelContext.Provider value={value}>
      <SidePanelContentContext.Provider value={content}>
        {children}
      </SidePanelContentContext.Provider>
    </SidePanelContext.Provider>
  );
}

/**
 * Docked panels reserve space beside the page; overlay panels leave its width
 * unchanged. Both slide in from the right without a backdrop.
 * On phones and tablets the panel fills the viewport above navigation, below
 * dialogs. Dock only when there is room for both the sidebar and page content.
 */
export function SidePanelLayout({ children }: { children: ReactNode }) {
  const { isOpen, presentation } = useSidePanel();
  const content = useContext(SidePanelContentContext);

  return (
    <div className="relative flex min-h-0 w-full min-w-0 flex-1">
      <div
        className={cn(
          // min-h-0 lets full-height routes (e.g. conversations) shrink to the
          // viewport so their inner scroll areas scroll instead of being
          // clipped by the overflow-hidden inset.
          'flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-200 ease-out',
          isOpen && presentation === 'docked' && 'lg:pr-[29rem]'
        )}
      >
        {children}
      </div>
      <aside
        data-testid="side-panel"
        data-state={isOpen ? 'open' : 'closed'}
        aria-hidden={!isOpen}
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l bg-background shadow-xl transition-transform duration-200 ease-out overflow-hidden lg:inset-y-4 lg:right-4 lg:z-30 lg:w-[28rem] lg:max-w-[90vw] lg:rounded-2xl lg:border',
          isOpen ? 'translate-x-0' : 'translate-x-[calc(100%+1rem)]'
        )}
      >
        {content}
      </aside>
    </div>
  );
}
